/**
 * Neural Network Effect
 *
 * 意識の海 - ニューラルネットワーク可視化
 * サイバーパンク・ネオンカラー（パープル系）
 */

import * as THREE from 'three';
import type { SystemObject } from '../types.js';

/**
 * Create neural network visualization
 */
export function createNeuralNetwork(
  scene: THREE.Scene,
  systemsRef: React.MutableRefObject<SystemObject[]>
): void {
  const nodeCount = 400;
  const nodes: THREE.Vector3[] = [];
  const connections: { from: number; to: number }[] = [];

  // ノードを配置（より大きな範囲に）
  for (let i = 0; i < nodeCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const radius = 600 + Math.random() * 400;

    nodes.push(new THREE.Vector3(
      radius * Math.sin(phi) * Math.cos(theta),
      radius * Math.sin(phi) * Math.sin(theta) * 0.8,
      radius * Math.cos(phi) - 200
    ));
  }

  // 接続を作成（近いノード同士）
  for (let i = 0; i < nodeCount; i++) {
    for (let j = i + 1; j < nodeCount; j++) {
      if (nodes[i].distanceTo(nodes[j]) < 250 && Math.random() > 0.75) {
        connections.push({ from: i, to: j });
      }
    }
  }

  // ノードのジオメトリ
  const nodeGeo = new THREE.BufferGeometry();
  const nodePositions = new Float32Array(nodeCount * 3);
  const nodePhases = new Float32Array(nodeCount);

  nodes.forEach((node, i) => {
    nodePositions[i * 3] = node.x;
    nodePositions[i * 3 + 1] = node.y;
    nodePositions[i * 3 + 2] = node.z;
    nodePhases[i] = Math.random() * Math.PI * 2;
  });

  nodeGeo.setAttribute('position', new THREE.BufferAttribute(nodePositions, 3));
  nodeGeo.setAttribute('phase', new THREE.BufferAttribute(nodePhases, 1));

  const nodeMat = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, beat: { value: 0 } },
    vertexShader: `
      attribute float phase;
      varying float vPhase;
      varying float vActive;
      uniform float time;
      uniform float beat;
      void main() {
        vPhase = phase;
        // ニューロン発火
        vActive = step(0.7, sin(time * 2.0 + phase));
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = (8.0 + vActive * 15.0 + beat * 10.0) * (500.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying float vPhase;
      varying float vActive;
      uniform float time;
      void main() {
        float dist = length(gl_PointCoord - vec2(0.5));
        if (dist > 0.5) discard;
        float glow = exp(-dist * 4.0);
        // 🎨 サイバーパンク・ネオンカラー（パープル系）
        vec3 baseColor = vec3(0.5, 0.0, 0.7);   // ディープパープル
        vec3 activeColor = vec3(0.7, 0.2, 1.0); // ブライトバイオレット
        vec3 cyanPulse = vec3(0.0, 1.0, 1.0);   // ネオンシアン
        vec3 color = mix(baseColor, activeColor, vActive);
        // シアンのパルスをアクセントに
        color = mix(color, cyanPulse, vActive * sin(vPhase + time * 5.0) * 0.3);
        float alpha = glow * (0.6 + vActive * 0.4);
        gl_FragColor = vec4(color * (1.2 + glow), alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });

  const nodePoints = new THREE.Points(nodeGeo, nodeMat);

  // 接続線
  const linePositions: number[] = [];
  connections.forEach(conn => {
    linePositions.push(nodes[conn.from].x, nodes[conn.from].y, nodes[conn.from].z);
    linePositions.push(nodes[conn.to].x, nodes[conn.to].y, nodes[conn.to].z);
  });

  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));

  const lineMat = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 } },
    vertexShader: `
      varying vec3 vPosition;
      void main() {
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vPosition;
      uniform float time;
      void main() {
        // 🎨 サイバーパンク・シナプス信号（パープル系）
        float signal = sin(length(vPosition) * 0.02 - time * 3.0) * 0.5 + 0.5;
        vec3 violet = vec3(0.6, 0.1, 0.9);       // ブライトバイオレット
        vec3 cyan = vec3(0.0, 0.9, 1.0);         // ネオンシアン
        vec3 purple = vec3(0.4, 0.0, 0.7);       // ディープパープル
        vec3 color = mix(purple, violet, signal);
        // シアンの電気パルス
        float pulse = smoothstep(0.4, 0.6, signal);
        color = mix(color, cyan, pulse * 0.4);
        gl_FragColor = vec4(color, 0.2 + signal * 0.25);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });

  const lines = new THREE.LineSegments(lineGeo, lineMat);

  const neuralGroup = new THREE.Group() as unknown as SystemObject;
  neuralGroup.add(nodePoints);
  neuralGroup.add(lines);

  neuralGroup.userData.update = (ctx) => {
    nodeMat.uniforms.time.value = ctx.time;
    nodeMat.uniforms.beat.value = ctx.beat.intensity;
    lineMat.uniforms.time.value = ctx.time;
    neuralGroup.rotation.y = ctx.time * 0.05;
  };

  scene.add(neuralGroup);
  systemsRef.current.push(neuralGroup);
}
